import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../../utils/cn";
import { NAV_ITEMS, GALLERY_PATH } from "../../config/nav";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { canAccess } from "../../config/roles";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onToggle, onNavigate }: SidebarProps) {
  const { role } = useAuth();
  const { businessName, effectiveLogo } = useBranding();
  const location = useLocation();
  const visibleItems = NAV_ITEMS.filter((item) => canAccess(role, item.key));

  const nameParts = (businessName || "M.K. FINANCIAL SERVICES").trim().split(" ");
  const firstName = nameParts[0] || "";
  const restName = nameParts.slice(1).join(" ");

  return (
    <aside className="no-print flex h-full flex-col border-r border-background-200 bg-background-50">
      {/* Brand */}
      <div className={cn("flex h-14 items-center gap-2 border-b border-background-100 px-3", collapsed ? "justify-center" : "px-4")}>
        {!collapsed && (
          <NavLink to="/" onClick={onNavigate} className="flex items-center gap-2.5 min-w-0">
            <span className="flex-shrink-0">
              <img src={effectiveLogo} alt={businessName} className="h-7 w-7 object-contain rounded" />
            </span>
            <span className="text-sm font-bold tracking-tight text-foreground-900 leading-tight truncate" title={businessName}>
              {firstName} {restName && <span className="text-primary-600">{restName}</span>}
            </span>
          </NavLink>
        )}
        {collapsed && (
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-100 shadow-sm" title={businessName}>
            <img src={effectiveLogo} alt={businessName} className="h-6 w-6 object-contain rounded" />
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-0.5">
          {visibleItems.map((item) => {
            const isSubActive = item.path !== "/" && (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
            return (
              <li key={item.key}>
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  onClick={onNavigate}
                  title={item.label}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      collapsed && "justify-center px-0",
                      isActive || isSubActive
                        ? "bg-primary-600 text-white font-semibold shadow-sm hover:bg-primary-700"
                        : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                    )
                  }
                >
                  {({ isActive }) => {
                    const active = isActive || isSubActive;
                    return (
                      <>
                        <i className={cn("text-lg", item.icon, active ? "text-white" : "text-foreground-400")} />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </>
                    );
                  }}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="mt-4 border-t border-background-100 pt-3 space-y-0.5">
         
          {/* <NavLink
            to={GALLERY_PATH}
            onClick={onNavigate}
            title="Component Gallery"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                collapsed && "justify-center px-0",
                isActive
                  ? "bg-primary-600 text-white font-semibold shadow-sm hover:bg-primary-700"
                  : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
              )
            }
          >
            {({ isActive }) => (
              <>
                <i className={cn("text-lg ri-layout-grid-line", isActive ? "text-white" : "text-foreground-400")} />
                {!collapsed && <span className="truncate">Component Gallery</span>}
              </>
            )}
          </NavLink> */}

          <NavLink
            to="/profile"
            onClick={onNavigate}
            title="My Profile"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                collapsed && "justify-center px-0",
                isActive
                  ? "bg-primary-600 text-white font-semibold shadow-sm hover:bg-primary-700"
                  : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
              )
            }
          >
            {({ isActive }) => (
              <>
                <i className={cn("text-lg ri-user-3-line", isActive ? "text-white" : "text-foreground-400")} />
                {!collapsed && <span className="truncate">My Profile</span>}
              </>
            )}
          </NavLink>
          
        </div>
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-background-100 p-2">
        <button
          type="button"
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100"
        >
          <i className={cn("text-lg text-foreground-400", collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line")} />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}